<template>
  <div class="tx-signing">
    <c-modal
      :persistent="true"
      :value="!processing && !txHash"
      @onClosed="$emit('cancel')"
    >
      <div class="heading-4 font-weight-medium">{{ title }} Confirmation</div>
      <div class="d-flex mt-8">
        <div
          class="flex-2 text-left text-5 font-weight-medium text-uppercase gray--text"
        >
          Project:
        </div>
        <div class="flex-3 text-3">
          {{ projectName || project.text }}
        </div>
      </div>
      <slot></slot>
      <div class="d-flex mt-3 align-center" v-if="metadata.amount">
        <div
          class="flex-2 text-left text-5 font-weight-medium text-uppercase gray--text"
        >
          Amount:
        </div>
        <div class="flex-3 text-3">
          {{ metadata.amount | denomFormat }}
          {{ (metadata.token || project.staking.token) | uppercase }}
        </div>
      </div>
      <div class="d-flex mt-3 align-center" v-if="metadata.vote && metadata.id">
        <div
          class="flex-2 text-left text-5 font-weight-medium text-uppercase gray--text"
        >
          Proposal Id:
        </div>
        <div class="flex-3 text-3">
          {{ metadata.id }}
        </div>
      </div>
      <div class="d-flex mt-3 align-center" v-if="metadata.vote">
        <div
          class="flex-2 text-left text-5 font-weight-medium text-uppercase gray--text"
        >
          Your Vote:
        </div>
        <div class="flex-3 text-3">
          {{ metadata.vote }}
        </div>
      </div>
      <div class="d-flex mt-3 align-center">
        <div
          class="flex-2 text-left text-5 font-weight-medium text-uppercase gray--text"
        >
          Transaction Fee:
        </div>
        <div class="flex-3 text-3">
          {{ gasOption.gasPrice | denomFormat }}
          {{ feeToken || project.staking.token | uppercase }}
        </div>
      </div>
      <br />
      <v-divider class="border-secondary"></v-divider>
      <br />
      <div class="text-6 darkgray--text smoke rounded-10 pa-5">
        Confirming will require you to sign this transaction and that you agree
        to the
        <a
          class="text-decoration-none"
          href="https://www.unagii.com/legal/terms"
          target="_blank"
          rel="noopener noreferrer"
          >Terms & Conditions</a
        >.
      </div>
      <br />
      <div class="d-flex justify-end align-center">
        <div
          class="text-4 font-weight-medium gray--text clickable"
          @click="$emit('cancel')"
        >
          Cancel
        </div>
        <c-btn @click="confirm" :disabled="!rawTx" class="ml-5">Confirm</c-btn>
      </div>
    </c-modal>
    <c-loading-dialog
      v-if="processing"
      title="Processing transaction"
      class="tx-signing-loading-dialog"
    />
    <tx-sent :txHash="txHash" v-if="txHash" @next="onNext" />
  </div>
</template>
<script>
import { mapGetters } from "vuex";
import AppConst from "@/common/constants";
import TxSent from "../TxSent.vue";
import Config from "@/common/config";
const { walletconnect } = Config;
const TX_TIMEOUT = 120000;

export default {
  components: { TxSent },
  name: "TendermintTxSigning",
  props: {
    title: String,
    name: String,
    getRawTx: Function,
    successMesssage: String,
    metadata: Object,
    projectName: String,
    feeToken: String,
    gasOption: Object,
  },
  data: () => ({
    rawTx: null,
    processing: false,
    txHash: null,
  }),
  computed: {
    ...mapGetters({
      activeBackend: "walletUtils/getActiveBackend",
      validBackend: "walletUtils/getValidBackend",
      signableWallet: "userWallets/signableWallet",
      project: "projectConfig/latestProject",
      accountInfo: "staking/tendermint/accountInfo",
    }),
    accountSelected() {
      return this.signableWallet;
    },
    accountMetadata() {
      return this.accountSelected.metadata || {};
    },
    accountHdPath() {
      return this.accountMetadata.hdPath || "";
    },
  },
  methods: {
    async connectWallet() {
      this.$store.dispatch(
        "walletUtils/setBackend",
        this.accountMetadata.type || this.accountSelected.type
      );
      try {
        await this.$store.dispatch("walletUtils/importBackend", {
          coinName: this.accountSelected.coin_network,
          keystationOptions: this.project.config.keystation,
          keplrOptions: {
            project: this.project.name,
            chainId: this.project.staking.chainId,
            ...this.project.config.keplr,
          },
          leapOptions: {
            project: this.project.name,
            chainId: this.project.staking.chainId,
            ...this.project.config.keplr,
          },
          appName: await this.$store.dispatch("walletUtils/getLedgerAppName", {
            coinName: this.accountSelected.coin_network,
            hdPath: this.accountHdPath,
          }),
          metamaskChainParams: this.project.config.metamaskChainParams,
          walletconnect: {
            projectId: walletconnect.projectId,
            network: this.project.config.network,
            accountAddress: this.accountSelected.address,
            accountPubkey: this.accountInfo.result?.publicKey
          },
        });
        if (this.validBackend && this.validBackend !== true) {
          throw new Error(this.validBackend);
        }
        const account = await this.activeBackend.getAccount(
          this.accountMetadata
        );
        const { address: accountAddress } = await account.getAddress();
        if (
          accountAddress.toLowerCase() !==
          this.accountSelected.address.toLowerCase()
        ) {
          throw new Error(
            `Please use the correct account to sign the transaction.\nExpected address: ${this.accountSelected.address}.\nActual address: ${accountAddress}.`
          );
        }
        return account;
      } catch (error) {
        if ("function" === typeof this.activeBackend?.cleanUp) {
          this.activeBackend.cleanUp();
        }
        this.onError(error);
      }
    },
    async confirm() {
      this.processing = true;
      let errorCode = AppConst.TX_ERROR.CONNECT_TIMEOUT;
      setTimeout(() => {
        if (this.processing) {
          this.onError(
            new Error(
              `Transaction timeout. Please try again.\n(Error code: ${errorCode})`
            )
          );
        }
      }, TX_TIMEOUT);
      const account = await this.connectWallet();
      if (account) {
        try {
          errorCode = AppConst.TX_ERROR.SIGN_TIMEOUT;
          let {
            pub_key,
            signature,
            txHash,
            signed,
            tx_bytes,
          } = await account.signTransaction(this.rawTx, {
            address: this.accountSelected.address,
            ethChainId: Number(
              this.project.config.metamaskChainParams?.chainId
            ),
          });
          if ((signature || tx_bytes) && !txHash) {
            errorCode = AppConst.TX_ERROR.BROADCAST_TIMEOUT;
            const data = await this.broadcastTx({
              signature: {
                pub_key,
                signature,
              },
              rawTx: signed || this.rawTx,
              metadata: {
                type: this.name,
                ...(this.metadata || {}),
              },
              tx_bytes,
            });
            if (data.error) {
              return this.onError(new Error(data.error));
            }
            txHash = data.txhash;
          } else {
            this.createTx({
              txHash,
              metadata: {
                type: this.name,
                ...(this.metadata || {}),
              },
            });
          }
          this.onSuccess(txHash);
        } catch (error) {
          this.onError(error);
        }
      }
    },
    async broadcastTx(payload) {
      return this.$store.dispatch("staking/tendermint/broadcastTx", payload);
    },
    async createTx(payload) {
      return this.$store.dispatch("staking/tendermint/createTx", payload);
    },
    onError(error) {
      this.processing = false;
      this.$store.dispatch("addAlert", {
        type: "error",
        title: "Error",
        message: error?.message || "Something went wrong.",
      });
      this.$emit("cancel");
    },
    onSuccess(txHash) {
      this.processing = false;
      this.txHash = txHash;
    },
    async onNext() {
      this.$emit("onSuccess", this.txHash);
    },
  },
  async mounted() {
    try {
      this.rawTx = await this.getRawTx();
    } catch (error) {
      this.$store.dispatch("addAlert", {
        type: "error",
        title: "Error",
        message: error.message,
      });
      this.$emit("cancel");
    }
  },
};
</script>
